import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { tursoAPI as propertyAPI } from '../services/tursoApi';
import { inquiryService } from '../services/inquiryService';
import { MapPin, Bed, Bath, Ruler, Car, CalendarDays, CheckCircle, XCircle } from 'lucide-react';

import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const redIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const MapControls = () => {
  const map = useMap();

  return (
    <div className="absolute top-20 left-4 z-[1000] flex flex-col bg-white rounded-md shadow-md border border-gray-300">
      <button 
        onClick={() => map.zoomIn()} 
        className="w-8 h-8 text-xl font-bold text-gray-700 hover:bg-gray-100 border-b border-gray-300"
        aria-label="Zoom in"
      >
        +
      </button>
      <button 
        onClick={() => map.zoomOut()} 
        className="w-8 h-8 text-xl font-bold text-gray-700 hover:bg-gray-100"
        aria-label="Zoom out"
      >
        -
      </button>
    </div>
  );
};

const PropertyDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImage, setActiveImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); 

  const hasIncrementedRef = useRef(false);
  const mapRef = useRef(null); 

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const data = await propertyAPI.getPropertyBySlug(slug);
        if (!data) throw new Error('Property not found');

        const parsedImages = Array.isArray(data.images) 
          ? data.images 
          : (typeof data.images === 'string' ? JSON.parse(data.images) : []);
        const parsedAmenities = Array.isArray(data.amenities) 
          ? data.amenities 
          : (typeof data.amenities === 'string' ? JSON.parse(data.amenities) : []);

        let combinedSlides = [...parsedImages];
        if (data?.location?.coordinates) {
          combinedSlides.push({
            src: 'map-placeholder',
            alt: 'Property Location Map',
          });
        }

        const formattedProperty = {
          ...data,
          images: combinedSlides,
          amenities: parsedAmenities,
          features: data.features || {},
          location: data.location || {},
          propertyType: data.propertyType || null,
          yearBuilt: data.yearBuilt || null,
          furnishing: data.furnishing || null,
        };

        setProperty(formattedProperty);
        setError(null);

        if (formattedProperty.id && !hasIncrementedRef.current) {
          hasIncrementedRef.current = true; 
          await propertyAPI.incrementViews(formattedProperty.id);
        }

      } catch (err) {
        console.error("Error fetching property:", err); 
        setError('This property is not available or has been removed.');
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [slug]);

  const formatPrice = (price) => {
    if (price >= 10000000) { 
      return `₹${(price / 10000000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })} Cr`;
    } else if (price >= 100000) { 
      return `₹${(price / 100000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })} L`;
    }
    return `₹${price?.toLocaleString() || 'N/A'}`; 
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setInquiryForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await inquiryService.createPropertyInquiry({
        property_id: property.id,
        property_url: window.location.href,
        name: inquiryForm.name,
        email: inquiryForm.email,
        phone: inquiryForm.phone,
        message: inquiryForm.message
      });
      setSubmitStatus('success');
      setInquiryForm({ name: '', email: '', phone: '', message: '' }); 
    } catch (error) {
      console.error('Error sending contact request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000); 
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-background-light py-20 flex items-center justify-center">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto text-center bg-white rounded-2xl shadow-xl p-10 border border-gray-100"
          >
            <h1 className="text-4xl font-extrabold text-red-600 mb-6 flex items-center justify-center gap-3">
              <XCircle size={40} /> Property Not Found
            </h1>
            <p className="text-lg text-text-secondary mb-8">{error}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/properties')}
                className="px-8 py-4 bg-primary-500 text-white rounded-xl shadow-lg hover:bg-primary-600 transition-all duration-300 font-semibold text-lg"
              >
                Browse All Properties
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-gray-200 text-text-primary rounded-xl shadow-md hover:bg-gray-300 transition-all duration-300 font-semibold text-lg"
              >
                Go to Homepage
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }
  
  const hasPhoto = property.images[activeImage]?.src !== 'map-placeholder';

  const photoSlides = property.images.filter(img => img.src !== 'map-placeholder').map(img => ({
      src: img.url,
      alt: img.alt
  }));

  const findLightboxIndex = () => {
      const activeImageSrc = property.images[activeImage]?.url;
      const photoIndex = photoSlides.findIndex(slide => slide.src === activeImageSrc);
      return photoIndex !== -1 ? photoIndex : 0;
  };
  
  const handleOpenLightbox = (index) => {
    const photoIndex = photoSlides.findIndex(slide => slide.src === property.images[index].url);
    if (photoIndex !== -1) {
      setLightboxIndex(photoIndex);
      setIsLightboxOpen(true);
      setLightboxLoading(true);
    }
  };


  return (
    <div className="min-h-screen bg-background-light font-sans text-text-primary">
      {/* Hero Image Section */}
      <div 
        className="relative h-[65vh] md:h-[95vh] bg-gray-900 overflow-hidden" 
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 transition-opacity duration-500" 
        >
          {property.images[activeImage]?.src === 'map-placeholder' ? (
            <div className="w-full h-full relative">
              <MapContainer
                center={[property.location.coordinates.lat, property.location.coordinates.lng]}
                zoom={14}
                scrollWheelZoom={true}
                className="w-full h-full z-0"
                zoomControl={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker
                  position={[property.location.coordinates.lat, property.location.coordinates.lng]}
                  icon={redIcon}
                >
                  <Popup>
                    {property.title} <br />
                    {property.location.address}, {property.location.city}
                  </Popup>
                </Marker>
                <MapControls />
              </MapContainer>
            </div>
          ) : (
            <img
              src={property.images[activeImage]?.url || 'https://via.placeholder.com/1200x800?text=No+Image'}
              alt={property.images[activeImage]?.alt || property.title}
              className="w-full h-full object-cover object-center"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent via-black/30 pointer-events-none" />
        </motion.div>
        
        {/* Clickable Overlay for Lightbox */}
        {property.images[activeImage]?.src !== 'map-placeholder' && (
          <div 
            className="absolute inset-0 z-20 cursor-zoom-in"
            onClick={() => {
              const activeUrl = property.images[activeImage]?.url;
              const photoIndex = photoSlides.findIndex(p => p.src === activeUrl);
              if (photoIndex !== -1) {
                setLightboxIndex(photoIndex);
                setIsLightboxOpen(true);
              }
            }}
          />
        )}

        <div className="absolute bottom-0 left-0 right-0 p-8 z-10 text-white pointer-events-none">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-4xl md:text-5xl font-extrabold drop-shadow-lg mb-2"
          >
            {property.title}
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-xl md:text-2xl font-bold text-primary-300 drop-shadow-md mb-4"
          >
            {formatPrice(property.price)}
          </motion.p>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="text-lg text-gray-300 flex items-center gap-2 drop-shadow-md"
          >
            <MapPin size={20} className="text-primary-400" />
            {property.location.address}, {property.location.city}, {property.location.state}, {property.zipCode}
          </motion.p>
        </div>

        {/* Desktop Image Thumbnails */}
        <div className="absolute bottom-8 right-8 z-20 hidden md:block">
          <div className="flex flex-col space-y-3">
            {property.images.map((image, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  setActiveImage(index);
                }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.3 }}
                whileHover={{ scale: 1.05, boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}
                className={`w-20 h-20 rounded-lg overflow-hidden border-3 transition-all duration-300 transform-gpu ${
                  activeImage === index ? 'border-primary-500 shadow-md' : 'border-transparent opacity-70 hover:opacity-100'
                } hover:border-primary-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400`}
              >
                {image.src === 'map-placeholder' ? (
                  <div className="flex items-center justify-center w-full h-full bg-gray-200 text-gray-500">
                    <MapPin size={32} />
                  </div>
                ) : (
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Mobile Image Thumbnails */}
        <div className="absolute bottom-4 right-4 z-20 md:hidden">
            <div className="flex overflow-x-auto space-x-3 pb-1 scrollbar-hide justify-end">
                {property.images.map((image, index) => (
                    <motion.button
                        key={index}
                        onClick={() => {
                          setActiveImage(index);
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 * index, duration: 0.3 }}
                        whileHover={{ scale: 1.05 }}
                        className={`flex-shrink-0 w-11 h-11 rounded-lg overflow-hidden border-3 transition-all duration-300 ${
                            activeImage === index ? 'border-primary-500 shadow-md' : 'border-transparent opacity-70 hover:opacity-100'
                        } hover:border-primary-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400`}
                    >
                        {image.src === 'map-placeholder' ? (
                            <div className="flex items-center justify-center w-full h-full bg-gray-200 text-gray-500">
                                <MapPin size={24} />
                            </div>
                        ) : (
                            <img
                                src={image.url}
                                alt={image.alt}
                                className="w-full h-full object-cover"
                            />
                        )}
                    </motion.button>
                ))}
            </div>
        </div>
      </div>

      {isLightboxOpen && (
        <>
          <Lightbox
            open={isLightboxOpen}
            close={() => setIsLightboxOpen(false)}
            slides={photoSlides}
            index={lightboxIndex}
            on={{
              view: ({ index }) => {
                const newActiveImageUrl = photoSlides[index]?.src;
                if (newActiveImageUrl) {
                  const newActiveImageIndex = property.images.findIndex(img => img.url === newActiveImageUrl);
                  if (newActiveImageIndex !== -1) {
                    setActiveImage(newActiveImageIndex);
                  }
                }
              },
              loading: () => setLightboxLoading(true),
              loaded: () => setLightboxLoading(false)
            }}
            animation={{ swipe: 300 }}
            controller={{ closeOnBackdropClick: true }}
            carousel={{ padding: 0, spacing: 0 }}
          />
          {lightboxLoading && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
            </div>
          )}
        </>
      )}

      {/* Main Content Area */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Property Details Column */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100 mb-12"
            >
              {/* Key Features (Bedrooms, Bathrooms, Sq Ft, Parking) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b pb-8 border-gray-100">
                <div className="flex flex-col items-center justify-center p-4 bg-primary-50 rounded-xl text-primary-800">
                  <Bed size={28} className="mb-2 text-primary-600" />
                  <div className="text-xl font-bold">{property.features?.bedrooms || 'N/A'}</div> 
                  <div className="text-sm text-gray-600">Bedrooms</div>
                </div>
                <div className="flex flex-col items-center justify-center p-4 bg-primary-50 rounded-xl text-primary-800">
                  <Bath size={28} className="mb-2 text-primary-600" />
                  <div className="text-xl font-bold">{property.features?.bathrooms || 'N/A'}</div> 
                  <div className="text-sm text-gray-600">Bathrooms</div>
                </div>
                <div className="flex flex-col items-center justify-center p-4 bg-primary-50 rounded-xl text-primary-800">
                  <Ruler size={28} className="mb-2 text-primary-600" />
                  <div className="text-xl font-bold">{property.features?.area?.toLocaleString() || 'N/A'}</div> 
                  <div className="text-sm text-gray-600">Sq Ft</div>
                </div>
                <div className="flex flex-col items-center justify-center p-4 bg-primary-50 rounded-xl text-primary-800">
                  <Car size={28} className="mb-2 text-primary-600" />
                  <div className="text-xl font-bold">{property.features?.parking || 'N/A'}</div> 
                  <div className="text-sm text-gray-600">Parking</div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-text-primary mb-5 border-b pb-3 border-gray-200">About This Property</h2>
                <p className="text-lg text-text-secondary leading-relaxed whitespace-pre-line">
                  {property.description}
                </p>
              </div>

              {/* Features & Amenities */}
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-text-primary mb-5 border-b pb-3 border-gray-200">Features & Amenities</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {property.amenities && property.amenities.length > 0 ? (
                    property.amenities.map((amenity, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 * index, duration: 0.4 }}
                        className="flex items-center text-text-secondary bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                      >
                        <CheckCircle size={20} className="text-green-500 mr-3 flex-shrink-0" />
                        <span className="font-medium text-gray-700">{amenity}</span>
                      </motion.div>
                    ))
                  ) : (
                    <p className="text-gray-500 italic col-span-full">No specific amenities listed.</p>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-text-primary mb-5 border-b pb-3 border-gray-200">Property Details</h2>
                <div className="bg-gray-50 rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-lg">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Property Type:</span>
                    <span className="text-primary-600 capitalize">{property.propertyType || 'N/A'}</span> 
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Status:</span>
                    <span className={`font-bold capitalize ${property.status === 'sold' ? 'text-red-500' : property.status === 'rented' ? 'text-orange-500' : 'text-green-600'}`}>
                      {property.status || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Year Built:</span>
                    <span className="text-gray-800 flex items-center gap-1"><CalendarDays size={18} />{property.features?.yearBuilt || 'N/A'}</span> 
                  </div>
              
                  <div className="flex justify-between items-center py-2">
                    <span className="font-semibold text-gray-700">Plot Area:</span>
                    <span className="text-gray-800">{property.features?.area?.toLocaleString() || 'N/A'} sqft</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-semibold text-gray-700">Last Updated:</span>
                    <span className="text-gray-800">{property.updatedAt ? new Date(property.updatedAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <aside className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 sticky top-8 flex flex-col space-y-8"
            >
              {/* Inquiry Form Section */}
              <div>
                <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">Inquire About This Property</h2>
                <form onSubmit={handleFormSubmit} className="flex flex-col space-y-5">
                  <div>
                    <label htmlFor="name" className="sr-only">Your Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your Name"
                      required
                      value={inquiryForm.name}
                      onChange={handleFormChange}
                      className="w-full border border-gray-300 rounded-xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-200 text-lg"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="sr-only">Your Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Your Email"
                      required
                      value={inquiryForm.email}
                      onChange={handleFormChange}
                      className="w-full border border-gray-300 rounded-xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-200 text-lg"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="sr-only">Your Phone Number</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="Your Phone Number"
                      value={inquiryForm.phone}
                      onChange={handleFormChange}
                      className="w-full border border-gray-300 rounded-xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-200 text-lg"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="sr-only">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder="I'm interested in this property. Please contact me with more details."
                      rows={6}
                      required
                      value={inquiryForm.message}
                      onChange={handleFormChange}
                      className="w-full border border-gray-300 rounded-xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-200 text-lg resize-y"
                    />
                  </div>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting}
                    className={`w-full py-4 rounded-xl font-bold text-xl transition-all duration-300 shadow-lg
                      ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-accent text-white hover:bg-accent/90'}`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                  </motion.button>
                </form>

                {submitStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`mt-6 p-4 rounded-lg flex items-center justify-center gap-3 font-semibold text-lg ${
                      submitStatus === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {submitStatus === 'success' ? (
                      <CheckCircle size={24} />
                    ) : (
                      <XCircle size={24} />
                    )}
                    {submitStatus === 'success' ? 'Inquiry sent successfully!' : 'Failed to send inquiry. Please try again.'}
                  </motion.div>
                )}
              </div>

              {/* Home Loan Eligibility Section */}
              <div className="border-t pt-8 border-gray-200">
                <div className="bg-gradient-to-r from-accent-light to-primary-light p-6 text-center rounded-xl">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Home Loan Calculator 💰
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Estimate your downpayment and monthly EMI with our easy-to-use calculator.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/loancalculator')}
                    className="w-full py-3 px-2 rounded-xl font-bold text-lg bg-accent text-white hover:bg-primary-700 transition-all duration-300 shadow-lg"
                  >
                    Calculate Now
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;